import TableToolbar from 'components/table/TableToolbar';
import React, { useEffect, useState } from 'react';
import MainTable from 'components/table/MainTable';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import SearchInput from 'components/search/SearchInput';
import { Image, Spin, message } from 'antd';
import NoImage from 'components/etc/NoImage';
import { Edit2, Trash } from 'iconsax-react';
import ActionButton from 'components/table/ActionButton';
import { LoadingOutlined } from '@ant-design/icons';
import { handleMessage } from 'utils/helper/message';
import { useParams } from 'react-router';
import DefaultButton from 'components/button/DefaultButton';
import { useNavigate, useLocation } from 'react-router-dom';
import DeleteModal from 'components/modal/DeleteModal';
import { loadingApis } from 'apis/story/loadingApis';
import LoadingImgCreate from './LoadingImgCreate';
/**
 * * 패키지 관리 - 목록
 * - 등록된 패키지가 특정 작품을 연결하는 과정 수행
 */

export interface IVisible {
  create: boolean;
  delete: boolean;
}

const LoadingImgTable = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState<IVisible>({
    create: false,
    delete: false,
  });
  const [searchData, setSearchData] = useState<string>('');
  const [loadingData, setLoadingData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const antLoadingIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

  const handleFetchData = async () => {
    if (projectId) {
      try {
        setLoading(true);
        const loadingRes = await loadingApis.loadingList(projectId);
        setLoadingData(loadingRes.list);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (loadingData) {
      setTableData(loadingData);
    }
  }, [loadingData]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  const handleLink = (record) => {
    navigate(`${record.id}/update`, {
      state: {
        record: record,
        project_type: location?.state?.record?.project_type,
      },
    });
  };

  // handle search input
  useEffect(() => {
    let filteredData = JSON.parse(JSON.stringify(loadingData));

    if (searchData && searchData.length) {
      filteredData = filteredData.filter((e) =>
        e.loading_name.toLowerCase().includes(searchData.toLowerCase()),
      );
    }

    setTableData(filteredData);
  }, [searchData, refresh]);

  const handleClickDeleteModal = (record) => {
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);

        await loadingApis.loadingDelete(projectId, selectedData.id);
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setSelectedData(record);
              handleLink(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t('storyEpisodeID.storyTabMenuList.loadingContent.table.name'),
      dataIndex: 'loading_name',
      width: '3rem',
    },
    {
      title: t('storyEpisodeID.storyTabMenuList.loadingContent.table.review'),
      dataIndex: 'image_url',
      width: '3rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage width='94px' height='80px' />
          ) : (
            <Image
              src={_}
              alt={record.image_name}
              height={80}
              width={94}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      dataIndex: '',
      width: '8rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  return (
    <>
      <TableToolbar>
        <SearchInput placeholder='Search...' setSearchData={setSearchData} />
        <div>
          <DefaultButton
            name='create'
            onClick={() => setVisible({ ...visible, create: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.loadingContent.header.button.create',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      {loading ? (
        <LoadingWrapper>
          <Spin indicator={antLoadingIcon} />
        </LoadingWrapper>
      ) : (
        <MainTable
          columns={columnsOrigin}
          dataSource={tableData}
          isPagination={false}
        />
      )}
      <LoadingImgCreate
        visible={visible}
        setVisible={setVisible}
        refresh={refresh}
        setRefresh={setRefresh}
      />
      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.loadingContent.modal.delete.title',
        )}
        content={t(
          'storyEpisodeID.storyTabMenuList.loadingContent.modal.delete.content',
        )}
        label={selectedData?.loading_name}
        handleDelete={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default LoadingImgTable;

const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
